import { ChangeEvent, useState } from 'react';
import styled from 'styled-components';
import { SearchModePreference } from '../blockSearch';

const STOPS = [SearchModePreference.CLASSIC, SearchModePreference.DEFAULT, SearchModePreference.NEW];

const STOP_LABELS: Record<SearchModePreference, string> = {
  [SearchModePreference.CLASSIC]: 'Classic',
  [SearchModePreference.DEFAULT]: 'Default',
  [SearchModePreference.NEW]: 'New',
};

// Sizes and colours follow semantic-ui's toggle checkbox, so the slider reads as the same kind of control
const TRACK_HEIGHT = '1.5rem';
const SLIDER_WIDTH = '9.5rem';
const THUMB_SHADOW = '0 1px 2px 0 rgba(34, 36, 38, 0.15), 0 0 0 1px rgba(34, 36, 38, 0.15) inset';
const FOCUS_SHADOW = `${THUMB_SHADOW}, 0 0 0 3px rgba(33, 133, 208, 0.35)`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

// Centred on the slider and its stop labels together, and sized to match their height
const Caption = styled.label`
  font-size: 1.07em;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
`;

const SliderColumn = styled.div`
  width: ${SLIDER_WIDTH};
`;

// Blue while the user's own choice (Classic or New) is in effect, grey on Default, like a toggle that is on or off
const Range = styled.input<{ $active: boolean }>`
  -webkit-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: ${TRACK_HEIGHT};
  margin: 0;
  background: transparent;
  cursor: pointer;

  &::-webkit-slider-runnable-track {
    height: ${TRACK_HEIGHT};
    border-radius: 500rem;
    background: ${({ $active }) => ($active ? '#2185d0' : 'rgba(0, 0, 0, 0.05)')};
    transition: background 0.3s ease;
  }

  &::-moz-range-track {
    height: ${TRACK_HEIGHT};
    border-radius: 500rem;
    background: ${({ $active }) => ($active ? '#2185d0' : 'rgba(0, 0, 0, 0.05)')};
    transition: background 0.3s ease;
  }

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: ${TRACK_HEIGHT};
    height: ${TRACK_HEIGHT};
    border-radius: 500rem;
    background: #fff linear-gradient(transparent, rgba(0, 0, 0, 0.05));
    box-shadow: ${THUMB_SHADOW};
  }

  &::-moz-range-thumb {
    width: ${TRACK_HEIGHT};
    height: ${TRACK_HEIGHT};
    border: none;
    border-radius: 500rem;
    background: #fff linear-gradient(transparent, rgba(0, 0, 0, 0.05));
    box-shadow: ${THUMB_SHADOW};
  }

  &:focus {
    outline: none;
  }

  &:focus-visible::-webkit-slider-thumb {
    box-shadow: ${FOCUS_SHADOW};
  }

  &:focus-visible::-moz-range-thumb {
    box-shadow: ${FOCUS_SHADOW};
  }
`;

const StopLabels = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 3px;
  font-size: 0.9em;
  line-height: 1.2;
`;

const StopLabel = styled.button<{ $selected: boolean }>`
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: pointer;
  color: ${({ $selected }) => ($selected ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.45)')};
  font-weight: ${({ $selected }) => ($selected ? 700 : 400)};

  &:hover {
    color: rgba(0, 0, 0, 0.87);
  }
`;

let sliderInstanceCounter = 0;

interface Props {
  /** The position shown */
  value: SearchModePreference;
  /** Whether Default means the new search, for the Default tooltip and screen readers */
  newSearchDefault: boolean;
  onChange: (preference: SearchModePreference) => void;
}

/** A three-stop slider for the blocks search: always classic, the environment's default, or always the new search experience. */
export function SearchModeSlider({ value, newSearchDefault, onChange }: Props) {
  const [id] = useState(() => `search-mode-slider-${++sliderInstanceCounter}`);
  const defaultName = newSearchDefault ? 'the new search experience' : 'the classic search';
  const describe = (stop: SearchModePreference) => {
    if (stop === SearchModePreference.DEFAULT) {
      return `Default (${defaultName})`;
    }

    return stop === SearchModePreference.NEW ? 'New search experience' : 'Classic search';
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const stop = STOPS[Number(event.target.value)];
    if (stop && stop !== value) {
      onChange(stop);
    }
  };

  return (
    <Wrapper>
      <Caption htmlFor={id}>Experience</Caption>
      <SliderColumn>
        <Range
          id={id}
          type="range"
          min={0}
          max={STOPS.length - 1}
          step={1}
          value={STOPS.indexOf(value)}
          onChange={handleChange}
          aria-valuetext={describe(value)}
          title={describe(value)}
          $active={value !== SearchModePreference.DEFAULT}
        />
        <StopLabels>
          {STOPS.map((stop) => (
            // The range input is the keyboard control; the labels are a mouse shortcut to each stop
            <StopLabel
              key={stop}
              type="button"
              tabIndex={-1}
              aria-hidden
              title={describe(stop)}
              $selected={stop === value}
              onClick={() => stop !== value && onChange(stop)}
            >
              {STOP_LABELS[stop]}
            </StopLabel>
          ))}
        </StopLabels>
      </SliderColumn>
    </Wrapper>
  );
}
